import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  Text,
  BlockStack,
  InlineStack,
  Box,
} from '@shopify/polaris';
import { useClinicAuth } from '../../contexts/ClinicAuthContext';

export default function ClinicLogin() {
  const navigate = useNavigate();
  const { login } = useClinicAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(async () => {
    if (!email.trim() || !password.trim()) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await login(email.trim(), password);
      navigate('/clinic');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [email, password, login, navigate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f6f6f7',
        padding: '20px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        <BlockStack gap="600">
          {/* Logo / Brand */}
          <Box paddingBlockEnd="200">
            <BlockStack gap="200" align="center">
              <Text as="h1" variant="headingXl" alignment="center">
                HealFaster
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                Clinic Portal
              </Text>
            </BlockStack>
          </Box>

          {/* Login Card */}
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Sign in to your clinic account
              </Text>

              {error && (
                <Banner tone="critical" onDismiss={() => setError('')}>
                  <p>{error}</p>
                </Banner>
              )}

              <div onKeyDown={handleKeyDown}>
                <FormLayout>
                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={setEmail}
                    autoComplete="email"
                    placeholder="clinic@example.com"
                    disabled={loading}
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={setPassword}
                    autoComplete="current-password"
                    disabled={loading}
                  />
                  <Button
                    variant="primary"
                    size="large"
                    fullWidth
                    onClick={handleSubmit}
                    loading={loading}
                  >
                    Sign In
                  </Button>
                </FormLayout>
              </div>
            </BlockStack>
          </Card>

          {/* Footer */}
          <InlineStack align="center">
            <Text as="p" variant="bodySm" tone="subdued">
              Need help? Contact your HealFaster account manager.
            </Text>
          </InlineStack>
        </BlockStack>
      </div>
    </div>
  );
}
